import { useState, useCallback, useRef, useEffect } from 'react';
import { TypingStats, Article, TypingHistory } from '../types';
import { saveHistory } from '../db/indexedDB';
import { smartWrapText, calculateErrorsCaseInsensitive, countTypableChars } from '../utils/textUtils';

const DEFAULT_DURATION = 60;

export interface LineState {
  target: string;
  input: string;          // 显示用（带标点和正确大小写）
  rawInput: string;       // 用户实际输入（仅字母数字空格）
  status: 'pending' | 'active' | 'completed';
  errors: number;
}

export interface TypingState {
  lines: LineState[];
  currentLineIndex: number;
  isStarted: boolean;
  isFinished: boolean;
  startTime: number | null;
  endTime: number | null;
  totalErrors: number;
  totalChars: number;
}

export function useTyping(article: Article | null, duration: number = DEFAULT_DURATION) {
  const [state, setState] = useState<TypingState>({
    lines: [],
    currentLineIndex: 0,
    isStarted: false,
    isFinished: false,
    startTime: null,
    endTime: null,
    totalErrors: 0,
    totalChars: 0,
  });

  const [stats, setStats] = useState<TypingStats>({
    wpm: 0,
    accuracy: 100,
    errorCount: 0,
    progress: 0,
    timeLeft: duration,
  });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedBeforePause, setElapsedBeforePause] = useState(0);

  // Initialize lines when article changes
  useEffect(() => {
    if (article) {
      // Use smart wrapping with 60 chars per line
      const wrappedLines = smartWrapText(article.content, 60);
      const lines: LineState[] = wrappedLines.map(line => ({
        target: line,
        input: '',
        rawInput: '',
        status: 'pending',
        errors: 0,
      }));
      if (lines.length > 0) {
        lines[0] = { ...lines[0], status: 'active' };
      }
      // Count only typable chars (exclude punctuation) for progress calculation
      const totalTypableChars = countTypableChars(article.content);
      setState({
        lines,
        currentLineIndex: 0,
        isStarted: false,
        isFinished: false,
        startTime: null,
        endTime: null,
        totalErrors: 0,
        totalChars: totalTypableChars,
      });
      setStats({
        wpm: 0,
        accuracy: 100,
        errorCount: 0,
        progress: 0,
        timeLeft: duration,
      });
      inputRefs.current = new Array(lines.length).fill(null);
    } else {
      setState({
        lines: [],
        currentLineIndex: 0,
        isStarted: false,
        isFinished: false,
        startTime: null,
        endTime: null,
        totalErrors: 0,
        totalChars: 0,
      });
    }
  }, [article, duration]);

  // Focus active input
  useEffect(() => {
    if (!state.isFinished && state.currentLineIndex < inputRefs.current.length) {
      const activeInput = inputRefs.current[state.currentLineIndex];
      if (activeInput && document.activeElement !== activeInput) {
        activeInput.focus();
      }
    }
  }, [state.currentLineIndex, state.isFinished]);

  const calculateWPM = useCallback((charsTyped: number, timeInSeconds: number): number => {
    if (timeInSeconds <= 0) return 0;
    const minutes = timeInSeconds / 60;
    const words = charsTyped / 5;
    return Math.round(words / minutes);
  }, []);

  const calculateAccuracy = useCallback((correct: number, total: number): number => {
    if (total === 0) return 100;
    return Math.round((correct / total) * 100);
  }, []);

  const updateStats = useCallback(() => {
    if (!state.isStarted || state.isFinished) return;

    const now = Date.now();
    const elapsed = state.startTime ? (now - state.startTime) / 1000 : 0;
    const timeLeft = Math.max(0, Math.round(duration - elapsed));

    const completedChars = state.lines.reduce((sum, line) => {
      if (line.status === 'completed') return sum + countTypableChars(line.target);
      return sum + line.rawInput.length;
    }, 0);

    const progress = state.totalChars > 0 ? Math.round((completedChars / state.totalChars) * 100) : 0;
    const totalTyped = state.lines.reduce((sum, line) => sum + line.rawInput.length, 0);
    const correctChars = totalTyped - state.totalErrors;

    setStats({
      wpm: calculateWPM(totalTyped, elapsed),
      accuracy: calculateAccuracy(correctChars, totalTyped),
      errorCount: state.totalErrors,
      progress,
      timeLeft,
    });

    if (timeLeft <= 0) {
      finishTyping();
    }
  }, [state, duration, calculateWPM, calculateAccuracy]);

  useEffect(() => {
    if (state.isStarted && !state.isFinished && !isPaused) {
      timerRef.current = setInterval(updateStats, 100);
      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [state.isStarted, state.isFinished, isPaused, updateStats]);

  // 监听页面可见性变化，暂停/继续计时器
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (state.isFinished || !state.isStarted) return;

      if (document.hidden) {
        // 页面失去焦点，暂停计时
        setIsPaused(true);
        const now = Date.now();
        const elapsed = state.startTime ? (now - state.startTime) / 1000 : 0;
        setElapsedBeforePause(elapsed);
      } else {
        // 页面获得焦点，继续计时
        // 调整 startTime，使 elapsed 从暂停前的值继续
        const now = Date.now();
        const newStartTime = now - elapsedBeforePause * 1000;
        setState((prev) => ({
          ...prev,
          startTime: newStartTime,
        }));
        setIsPaused(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isStarted, state.isFinished, state.startTime, elapsedBeforePause]);

  const finishTyping = useCallback(() => {
    if (state.isFinished) return;

    const endTime = Date.now();
    const elapsed = state.startTime ? (endTime - state.startTime) / 1000 : 0;

    const totalTyped = state.lines.reduce((sum, line) => {
      if (line.status === 'completed') return sum + countTypableChars(line.target);
      return sum + line.rawInput.length;
    }, 0);
    const correctChars = totalTyped - state.totalErrors;

    const finalStats: TypingStats = {
      wpm: calculateWPM(totalTyped, elapsed),
      accuracy: calculateAccuracy(correctChars, totalTyped),
      errorCount: state.totalErrors,
      progress: state.totalChars > 0 ? Math.round((totalTyped / state.totalChars) * 100) : 0,
      timeLeft: Math.max(0, Math.round(duration - elapsed)),
    };

    setStats(finalStats);
    setState((prev) => ({
      ...prev,
      isFinished: true,
      endTime,
    }));

    if (article && totalTyped > 0) {
      const history: TypingHistory = {
        id: `history-${Date.now()}`,
        articleId: article.id,
        articleTitle: article.title,
        wpm: finalStats.wpm,
        accuracy: finalStats.accuracy,
        errorCount: finalStats.errorCount,
        duration: Math.round(elapsed),
        totalChars: totalTyped,
        correctChars,
        createdAt: Date.now(),
      };
      saveHistory(history);
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, [state, article, duration, calculateWPM, calculateAccuracy]);

  const handleLineInput = useCallback((lineIndex: number, rawUserInput: string) => {
    if (!article || state.isFinished) return;

    if (!state.isStarted) {
      setState((prev) => ({ ...prev, isStarted: true, startTime: Date.now() }));
    }

    const target = state.lines[lineIndex].target;
    const targetTypableCount = countTypableChars(target);

    // 限制用户输入长度
    const trimmedRawInput = rawUserInput.slice(0, targetTypableCount);

    // 计算错误（大小写不敏感）
    const lineErrors = calculateErrorsCaseInsensitive(target, trimmedRawInput);

    setState((prev) => {
      const newLines = [...prev.lines];
      newLines[lineIndex] = {
        ...newLines[lineIndex],
        input: trimmedRawInput,    // 输入框显示用户原始输入（简化）
        rawInput: trimmedRawInput, // 原始用户输入
        errors: lineErrors,
      };

      // Recalculate total errors
      const newTotalErrors = newLines.reduce((sum, line) => sum + line.errors, 0);

      return {
        ...prev,
        lines: newLines,
        totalErrors: newTotalErrors,
      };
    });

    // Check if line is completed (compare only typable chars)
    if (trimmedRawInput.length === targetTypableCount) {
      // Line completed, move to next
      setState((prev) => {
        const newLines = [...prev.lines];
        newLines[lineIndex].status = 'completed';

        const nextIndex = lineIndex + 1;
        if (nextIndex < newLines.length) {
          newLines[nextIndex].status = 'active';
          return {
            ...prev,
            lines: newLines,
            currentLineIndex: nextIndex,
          };
        } else {
          // All lines completed
          return {
            ...prev,
            lines: newLines,
            isFinished: true,
          };
        }
      });

      // Check if all done
      if (lineIndex === state.lines.length - 1) {
        setTimeout(finishTyping, 100);
      }
    }
  }, [article, state.isFinished, state.isStarted, state.lines.length, finishTyping]);

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (article) {
      // Use smart wrapping with 60 chars per line
      const wrappedLines = smartWrapText(article.content, 60);
      const lines: LineState[] = wrappedLines.map(line => ({
        target: line,
        input: '',
        rawInput: '',
        status: 'pending',
        errors: 0,
      }));
      if (lines.length > 0) {
        lines[0] = { ...lines[0], status: 'active' };
      }
      // Count only typable chars (exclude punctuation)
      const totalTypableChars = countTypableChars(article.content);
      setState({
        lines,
        currentLineIndex: 0,
        isStarted: false,
        isFinished: false,
        startTime: null,
        endTime: null,
        totalErrors: 0,
        totalChars: totalTypableChars,
      });
      setStats({
        wpm: 0,
        accuracy: 100,
        errorCount: 0,
        progress: 0,
        timeLeft: duration,
      });
    }
  }, [article, duration]);

  const setInputRef = useCallback((index: number, el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  }, []);

  return {
    state,
    stats,
    setInputRef,
    handleLineInput,
    reset,
  };
}
